import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  finalize,
  first,
  forkJoin,
  tap,
} from 'rxjs';
import { InventoryItem } from 'src/app/core/models/inventory/inventory-item.interface';
import { InventoryItemOverviewFormModel } from 'src/app/core/models/inventory/inventory-item-overview-form.model';
import {
  InventoryItemOverviewEditableFieldName,
  InventoryItemOverviewField,
} from 'src/app/core/models/inventory/inventory-item-overview-view-model.interface';
import { InventoryItemWarehouseUpdate } from 'src/app/core/models/inventory/inventory-item-warehouse-update.interface';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import { CoreModule } from 'src/app/core/core.module';
import { INVENTORY_ITEM_TYPE_SERVICE } from 'src/app/core/enums/inventory-item-type.enum';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { isConcurrencyConflictError } from 'src/app/core/utils/api-error.util';

type InventoryItemWarehouseEditableFieldName = Extract<
  InventoryItemOverviewEditableFieldName,
  'stock' | 'baseUnitOfMeasureId' | 'warehouse' | 'length' | 'width' | 'height' | 'weight' | 'cubage'
>;

type InventoryItemWarehousePreviewUpdater = (
  item: InventoryItem,
  value: string,
) => Omit<InventoryItemWarehouseUpdate, 'expectedConcurrencyToken'>;

interface InventoryItemWarehouseViewModel {
  fields: Array<InventoryItemOverviewField & { controlName: InventoryItemWarehouseEditableFieldName }>;
  previewFieldLabelKey?: string;
  /**
   * True when the loaded inventory item is of type `Service`. Service items do not
   * maintain warehouse stock, so the warehouse tab surfaces a single read-only notice
   * instead of editable fields.
   */
  isServiceItem: boolean;
}

@Component({
  selector: 'app-inventory-item-warehouse-tab',
  templateUrl: './inventory-item-warehouse-tab.component.html',
  styleUrl: './inventory-item-warehouse-tab.component.scss',
  imports: [CommonModule, ReactiveFormsModule, CoreModule],
  providers: [InventoryItemOverviewFormModel],
  standalone: true,
})
export class InventoryItemWarehouseTabComponent implements OnChanges {
  readonly overviewForm = inject(InventoryItemOverviewFormModel);
  private readonly inventoryItemService = inject(InventoryItemService);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public itemId = '';
  @Input() public reloadToken = '';
  @Input() public inventoryItem: InventoryItem | null = null;
  @Input() public unitsOfMeasureOptions: UnitOfMeasureLookup[] = [];
  @Output() public readonly inventoryItemChange = new EventEmitter<InventoryItem>();
  @Output() public readonly concurrencyConflict = new EventEmitter<void>();

  public readonly inventoryItem$ = new BehaviorSubject<InventoryItem | null>(null);
  public readonly viewModel$ = new BehaviorSubject<InventoryItemWarehouseViewModel | null>(null);
  public readonly unitOfMeasureOptions$ = new BehaviorSubject<UnitOfMeasureLookup[]>([]);
  public isLoading = false;

  private readonly patchUpdaters: Record<
    InventoryItemWarehouseEditableFieldName,
    InventoryItemWarehousePreviewUpdater
  > = {
    stock: () => ({}),
    baseUnitOfMeasureId: (_, value) => ({ baseUnitOfMeasureId: this.toOptionalNumber(value) }),
    warehouse: (_, value) => ({ warehouse: this.toOptionalString(value) }),
    length: (_, value) => ({ length: this.toNullableNumber(value) }),
    width: (_, value) => ({ width: this.toNullableNumber(value) }),
    height: (_, value) => ({ height: this.toNullableNumber(value) }),
    weight: (_, value) => ({ weight: this.toNullableNumber(value) }),
    cubage: (_, value) => ({ cubage: this.toNullableNumber(value) }),
  };

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes['companyId'] &&
      !changes['itemId'] &&
      !changes['reloadToken'] &&
      !changes['inventoryItem'] &&
      !changes['unitsOfMeasureOptions']
    ) {
      return;
    }

    this.unitOfMeasureOptions$.next(this.unitsOfMeasureOptions);
    if (this.inventoryItem) {
      this.syncInventoryItemState(this.inventoryItem, undefined, this.unitsOfMeasureOptions);
      return;
    }

    this.loadInventoryItem();
  }

  public onPreviewFieldUpdate(fieldName: InventoryItemWarehouseEditableFieldName): void {
    const currentItem = this.inventoryItem$.getValue();
    if (!currentItem) {
      return;
    }

    const rawValue = this.overviewForm.form.controls[fieldName].value ?? '';
    const normalizedValue = this.normalizeControlValue(rawValue);
    const changes = this.withConcurrencyMetadata(currentItem, this.patchUpdaters[fieldName](currentItem, normalizedValue));
    const saveRequest =
      fieldName === 'stock'
        ? this.inventoryItemService.recountInventoryItemStock(this.companyId, this.itemId, {
            quantity: this.toNumber(normalizedValue),
            expectedConcurrencyToken: currentItem.concurrencyToken ?? '',
          })
        : this.inventoryItemService.updateInventoryItemWarehouse(this.companyId, this.itemId, changes);

    saveRequest
      .pipe(
        first(),
        tap((inventoryItem) => {
          this.syncInventoryItemState(inventoryItem, this.getFieldLabelKey(fieldName));
          this.inventoryItemChange.emit(inventoryItem);
        }),
        catchError((error) => {
          this.syncInventoryItemState(currentItem);
          if (isConcurrencyConflictError(error)) {
            this.concurrencyConflict.emit();
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private loadInventoryItem(): void {
    if (!this.companyId || !this.itemId) {
      return;
    }

    this.isLoading = true;
    forkJoin({
      inventoryItem: this.inventoryItemService.getInventoryItem(this.companyId, this.itemId),
      unitsOfMeasure: this.inventoryItemService.getUnitsOfMeasureLookup(this.companyId),
    })
      .pipe(
        first(),
        tap(({ inventoryItem, unitsOfMeasure }) => {
          this.unitOfMeasureOptions$.next(unitsOfMeasure);
          this.syncInventoryItemState(inventoryItem, undefined, unitsOfMeasure);
        }),
        catchError(() => {
          this.unitOfMeasureOptions$.next([]);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private syncInventoryItemState(
    inventoryItem: InventoryItem,
    previewFieldLabelKey?: string,
    unitsOfMeasure = this.unitOfMeasureOptions$.getValue(),
  ): void {
    this.inventoryItem$.next(inventoryItem);
    this.overviewForm.form.patchValue(
      {
        stock: this.toControlNumber(inventoryItem.stock),
        baseUnitOfMeasureId: inventoryItem.baseUnitOfMeasureId ?? null,
        warehouse: inventoryItem.warehouse ?? '',
        length: this.toControlNumber(inventoryItem.length),
        width: this.toControlNumber(inventoryItem.width),
        height: this.toControlNumber(inventoryItem.height),
        weight: this.toControlNumber(inventoryItem.weight),
        cubage: this.toControlNumber(inventoryItem.cubage),
      },
      { emitEvent: false },
    );
    this.viewModel$.next(this.buildViewModel(inventoryItem, previewFieldLabelKey, unitsOfMeasure));
  }

  private buildViewModel(
    item: InventoryItem,
    previewFieldLabelKey?: string,
    unitsOfMeasure = this.unitOfMeasureOptions$.getValue(),
  ): InventoryItemWarehouseViewModel {
    const isEditable = item.isArchived !== true;

    return {
      previewFieldLabelKey,
      isServiceItem: item.itemType === INVENTORY_ITEM_TYPE_SERVICE,
      fields: [
        {
          controlName: 'stock',
          label: 'COMPONENTS.GOODS.FIELDS.STOCK',
          value: this.formatNumberValue(item.stock),
          inputType: 'number',
          editable: isEditable,
        },
        {
          controlName: 'baseUnitOfMeasureId',
          label: 'COMPONENTS.GOODS.FIELDS.UNIT',
          value: this.resolveUnitDisplayName(item, unitsOfMeasure),
          inputType: 'select',
          editable: isEditable,
          selectItems: unitsOfMeasure.map((unitOfMeasure) => ({
            key: unitOfMeasure.displayName,
            value: unitOfMeasure.id,
          })),
        },
        {
          controlName: 'warehouse',
          label: 'COMPONENTS.GOODS.FIELDS.WAREHOUSE',
          value: item.warehouse ?? '',
          editable: isEditable,
        },
        {
          controlName: 'length',
          label: 'COMPONENTS.GOODS.FIELDS.LENGTH',
          value: this.formatNumberValue(item.length),
          inputType: 'number',
          editable: isEditable,
        },
        {
          controlName: 'width',
          label: 'COMPONENTS.GOODS.FIELDS.WIDTH',
          value: this.formatNumberValue(item.width),
          inputType: 'number',
          editable: isEditable,
        },
        {
          controlName: 'height',
          label: 'COMPONENTS.GOODS.FIELDS.HEIGHT',
          value: this.formatNumberValue(item.height),
          inputType: 'number',
          editable: isEditable,
        },
        {
          controlName: 'weight',
          label: 'COMPONENTS.GOODS.FIELDS.WEIGHT',
          value: this.formatNumberValue(item.weight),
          inputType: 'number',
          editable: isEditable,
        },
        {
          controlName: 'cubage',
          label: 'COMPONENTS.GOODS.FIELDS.CUBAGE',
          value: this.formatNumberValue(item.cubage),
          inputType: 'number',
          editable: isEditable,
        },
      ],
    };
  }

  private getFieldLabelKey(fieldName: InventoryItemWarehouseEditableFieldName): string {
    const labels: Record<InventoryItemWarehouseEditableFieldName, string> = {
      stock: 'COMPONENTS.GOODS.FIELDS.STOCK',
      baseUnitOfMeasureId: 'COMPONENTS.GOODS.FIELDS.UNIT',
      warehouse: 'COMPONENTS.GOODS.FIELDS.WAREHOUSE',
      length: 'COMPONENTS.GOODS.FIELDS.LENGTH',
      width: 'COMPONENTS.GOODS.FIELDS.WIDTH',
      height: 'COMPONENTS.GOODS.FIELDS.HEIGHT',
      weight: 'COMPONENTS.GOODS.FIELDS.WEIGHT',
      cubage: 'COMPONENTS.GOODS.FIELDS.CUBAGE',
    };

    return labels[fieldName];
  }

  private resolveUnitDisplayName(item: InventoryItem, unitsOfMeasure: UnitOfMeasureLookup[]): string {
    if (item.unit && item.unit.trim().length > 0) {
      return item.unit;
    }

    if (!item.baseUnitOfMeasureId) {
      return '';
    }

    return unitsOfMeasure.find((unitOfMeasure) => unitOfMeasure.id === item.baseUnitOfMeasureId)?.displayName ?? '';
  }

  private formatNumberValue(value?: number | null): string {
    if (value === null || value === undefined) {
      return '';
    }

    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
    }).format(value);
  }

  private toControlNumber(value?: number | null): string {
    if (value === null || value === undefined) {
      return '';
    }

    return value.toString();
  }

  private normalizeControlValue(value: unknown): string {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (value === null || value === undefined) {
      return '';
    }

    return `${value}`;
  }

  private toNumber(value: string): number {
    const parsedValue = Number.parseFloat(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  private toNullableNumber(value: string): number | null {
    if (!value) {
      return null;
    }

    const parsedValue = Number.parseFloat(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private toOptionalString(value: string): string | undefined {
    return value ? value : undefined;
  }

  private toOptionalNumber(value: string): number | undefined {
    if (!value) {
      return undefined;
    }

    const parsedValue = Number.parseInt(value, 10);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  private withConcurrencyMetadata<T extends object>(
    inventoryItem: InventoryItem,
    changes: T,
  ): T & { expectedConcurrencyToken: string } {
    return {
      ...changes,
      expectedConcurrencyToken: inventoryItem.concurrencyToken ?? '',
    };
  }
}
